/**
 * Soft-navigates between metafield-linked products (OZtrailAU pattern + Section Rendering API).
 *
 * - Fetches every #MainContent section via ?sections= (batched)
 * - replaceWith each #shopify-section-* (same swap as OZtrail)
 * - Re-inits executable scripts + bubbles shopify:section:load
 * - History replaceState (like variant picker) so Back leaves the PDP
 *   for the previous page (home/collection), not prior colour/length options
 * - Cache + prefetch for speed
 */
const SECTIONS_PER_REQUEST = 5;

const LinkedProductNavigation = {
  initialized: false,
  isHistoryNavigation: false,
  isLoading: false,
  /** Canonical path+search of the product currently on screen */
  displayedUrl: '',
  /** @type {AbortController | null} */
  abortController: null,
  /** @type {Map<string, Record<string, string>>} */
  sectionsCache: new Map(),
  /** @type {Set<string>} */
  prefetching: new Set(),

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.displayedUrl = this.normalizeUrl(this.getUrlFromLocation());

    if (window.location.pathname.includes('/products/')) {
      const url = this.getUrlFromLocation();
      window.history.replaceState({ linkedProductSelection: true, url }, '', url);
    }

    window.addEventListener('popstate', this.onPopState.bind(this));
  },

  /**
   * Browser back/forward. Linked options use replaceState, so Back usually
   * leaves the PDP (full navigation). Soft-load only if we stayed on a product URL.
   * @param {PopStateEvent} [_event]
   */
  onPopState(_event) {
    const targetUrl = this.getUrlFromLocation();

    // Left the product template (e.g. back to collection/home)
    if (!window.location.pathname.includes('/products/')) {
      this.displayedUrl = '';
      return;
    }

    if (!targetUrl) return;

    // Already showing this product (e.g. only ?variant= changed)
    if (this.isSameProduct(this.displayedUrl, targetUrl)) {
      this.syncPickerDataset(targetUrl);
      return;
    }

    this.isHistoryNavigation = true;
    this.loadProduct(targetUrl, { updateHistory: false });
  },

  getUrlFromLocation() {
    return `${window.location.pathname}${window.location.search}`;
  },

  /**
   * Path + search only, for history API + comparisons.
   * @param {string} url
   * @returns {string}
   */
  toHistoryUrl(url) {
    const parsed = this.toProductUrl(url);
    return `${parsed.pathname}${parsed.search}`;
  },

  getDisplayedProductUrl() {
    return this.displayedUrl || this.getUrlFromLocation();
  },

  /**
   * @param {string | undefined | null} url
   * @returns {string}
   */
  normalizeUrl(url) {
    if (!url) return '';
    try {
      const parsed = new URL(url, window.location.origin);
      return `${parsed.pathname}${parsed.search}`.replace(/\/$/, '') || '/';
    } catch {
      return String(url).split('#')[0].replace(/\/$/, '');
    }
  },

  /**
   * Product identity for picker/history — ignore ?variant= so size changes
   * are not treated as a different linked product.
   * @param {string | undefined | null} url
   * @returns {string}
   */
  toProductPath(url) {
    if (!url) return '';
    try {
      return new URL(url, window.location.origin).pathname.replace(/\/$/, '') || '/';
    } catch {
      return String(url).split('?')[0].split('#')[0].replace(/\/$/, '');
    }
  },

  /**
   * @param {string | undefined | null} urlA
   * @param {string | undefined | null} urlB
   * @returns {boolean}
   */
  isSameProduct(urlA, urlB) {
    const pathA = this.toProductPath(urlA);
    const pathB = this.toProductPath(urlB);
    return Boolean(pathA) && pathA === pathB;
  },

  /**
   * Absolute product URL without hash (path + search only on same origin).
   * @param {string} url
   * @returns {URL}
   */
  toProductUrl(url) {
    const parsed = new URL(url.split('#')[0], window.location.origin);
    return parsed;
  },

  /**
   * @returns {string[]} Section ids inside MainContent (without shopify-section- prefix)
   */
  getMainSectionIds() {
    const main = document.getElementById('MainContent');
    if (!main) return [];

    return Array.from(main.querySelectorAll('[id^="shopify-section-"]')).map((el) =>
      el.id.replace(/^shopify-section-/, '')
    );
  },

  /**
   * @param {string} url
   */
  syncPickerDataset(url) {
    const historyUrl = this.toHistoryUrl(url);
    document.querySelectorAll('linked-products-picker').forEach((element) => {
      element.dataset.currentProductUrl = historyUrl;
    });
  },

  /**
   * @param {string} url
   */
  updateCurrentProductUrl(url) {
    const historyUrl = this.toHistoryUrl(url);
    this.displayedUrl = this.normalizeUrl(historyUrl);
    this.syncPickerDataset(historyUrl);
  },

  /**
   * @param {string[]} sectionIds
   * @returns {string[][]}
   */
  chunkSectionIds(sectionIds) {
    /** @type {string[][]} */
    const chunks = [];
    for (let i = 0; i < sectionIds.length; i += SECTIONS_PER_REQUEST) {
      chunks.push(sectionIds.slice(i, i + SECTIONS_PER_REQUEST));
    }
    return chunks;
  },

  /**
   * Section Rendering API — fetch all MainContent sections for a product URL.
   * @param {string} productUrl
   * @param {string[]} sectionIds
   * @param {AbortSignal} [signal]
   * @returns {Promise<Record<string, string>>}
   */
  async fetchSections(productUrl, sectionIds, signal) {
    /** @type {Record<string, string>} */
    const merged = {};
    const base = this.toProductUrl(productUrl);

    for (const chunk of this.chunkSectionIds(sectionIds)) {
      const requestUrl = new URL(base.href);
      // Section Rendering uses `sections` — drop unrelated query noise except we keep none
      requestUrl.search = '';
      requestUrl.searchParams.set('sections', chunk.join(','));

      const response = await fetch(requestUrl.toString(), {
        signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) {
        throw new Error(`Section render failed: ${response.status}`);
      }

      const json = await response.json();
      Object.assign(merged, json);
    }

    return merged;
  },

  /**
   * @param {string} url
   */
  prefetch(url) {
    const normalizedUrl = url.split('#')[0];
    const key = this.normalizeUrl(normalizedUrl);
    const sectionIds = this.getMainSectionIds();

    if (!key || !sectionIds.length || this.sectionsCache.has(key) || this.prefetching.has(key)) return;
    if (this.isSameProduct(key, this.getUrlFromLocation())) return;

    this.prefetching.add(key);

    this.fetchSections(normalizedUrl, sectionIds)
      .then((sections) => {
        this.sectionsCache.set(key, sections);
      })
      .catch(() => {
        // Prefetch is best-effort
      })
      .finally(() => {
        this.prefetching.delete(key);
      });
  },

  /**
   * Boost.AI recommendation SSR blocks only ship empty mount points via Section Rendering.
   * Client JS fills them — do not replace these sections with SSR HTML (leaves blanks).
   * Refresh by updating TAE product_id + re-registering blocks (see reinitializeBoostRecommendations).
   * @param {Element} section
   * @param {string} [html]
   * @returns {boolean}
   */
  isBoostSection(section, html = '') {
    const sample = html || section.innerHTML || '';
    return Boolean(
      section.querySelector?.('[id^="boost-sd-widget-"]') ||
        section.querySelector?.('[class*="boost-sd__recommendation"]') ||
        /boost-sd-widget|boost-sd__recommendation|boost_ai_search_filter_recommendation/i.test(sample)
    );
  },

  /**
   * Apply Section Rendering API HTML — OZtrail replaceWith + reinit per section.
   * Boost sections are deferred (returned as ids) so we can refresh them via Boost's TAE API.
   * @param {Record<string, string>} sectionsHtmlById
   * @returns {{ boostSectionIds: string[] }}
   */
  applySections(sectionsHtmlById) {
    /** @type {string[]} */
    const boostSectionIds = [];

    for (const [sectionId, html] of Object.entries(sectionsHtmlById)) {
      if (!html) continue;

      const existing = document.getElementById(`shopify-section-${sectionId}`);
      if (!existing) continue;

      if (this.isBoostSection(existing, html)) {
        boostSectionIds.push(sectionId);
        continue;
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next =
        doc.getElementById(`shopify-section-${sectionId}`) ||
        doc.querySelector('.shopify-section') ||
        doc.body.firstElementChild;

      if (!next) continue;

      existing.replaceWith(next);
      this.reinitializeSection(next);
    }

    return { boostSectionIds };
  },

  /**
   * Read the product id now on the PDP (after main section swap).
   * @returns {string}
   */
  getCurrentProductId() {
    return (
      document.querySelector('variant-picker')?.dataset?.productId ||
      document.querySelector('product-form-component')?.dataset?.productId ||
      document.querySelector('[data-product-id]')?.getAttribute('data-product-id') ||
      ''
    );
  },

  /**
   * Boost TAE app instance (reads product_id from config.generalSettings).
   * @returns {any}
   */
  getBoostTAE() {
    return (
      window.boostWidgetIntegration?.TAEApp ||
      (typeof window.getBoostTAE === 'function' ? window.getBoostTAE() : null) ||
      null
    );
  },

  /**
   * Point Boost at the new product, then re-register recommendation blocks so they
   * reconnect + refetch (Boost ignores registerBlock if the id already exists).
   * @param {string[]} [_boostSectionIds]
   * @param {Record<string, string>} [_sectionsHtmlById]
   */
  reinitializeBoostRecommendations(_boostSectionIds = [], _sectionsHtmlById = {}) {
    const productId = this.getCurrentProductId();
    if (!productId) return;

    const tae = this.getBoostTAE();
    const integration = window.boostWidgetIntegration;

    // Boost RecommendationService.getCurrentProductId() reads THIS value
    try {
      if (tae?.config?.generalSettings) {
        tae.config.generalSettings.product_id = Number(productId);
        tae.config.generalSettings.page = 'product';
      }
      if (integration?.taeSettings) {
        // keep any mirrored settings in sync if present
      }
    } catch (error) {
      console.warn('Failed to update Boost product_id', error);
    }

    const reregisterWidgets = () => {
      const boostTAE = this.getBoostTAE();
      if (!boostTAE || typeof boostTAE.registerBlock !== 'function') return;

      document.querySelectorAll('[id^="boost-sd-widget-productpage-"]').forEach((widget) => {
        if (!(widget instanceof HTMLElement)) return;

        const blockId = widget.id;

        // registerBlock() no-ops when the id already exists — drop it first
        try {
          if (boostTAE.blocks?.[blockId]) delete boostTAE.blocks[blockId];
          if (integration?.blocks?.[blockId]) delete integration.blocks[blockId];
        } catch {
          // ignore
        }

        try {
          boostTAE.registerBlock({
            id: blockId,
            blockType: 'recommendation',
            document: widget,
            widgetId: blockId,
          });
        } catch (error) {
          console.warn('Boost registerBlock failed', blockId, error);
        }
      });

      window.dispatchEvent(new Event('scroll'));
      window.dispatchEvent(new Event('resize'));
    };

    // Let the main PDP morph settle, then re-bind Boost widgets
    requestAnimationFrame(() => {
      reregisterWidgets();
      // Second pass in case Boost modules weren't ready yet
      setTimeout(reregisterWidgets, 100);
    });
  },

  /**
   * @param {string} url
   * @param {{ updateHistory?: 'push' | 'replace' | false }} [options]
   */
  async loadProduct(url, { updateHistory = 'replace' } = {}) {
    const historyUrl = this.toHistoryUrl(url);
    const cacheKey = this.normalizeUrl(historyUrl);

    if (this.isSameProduct(historyUrl, this.getDisplayedProductUrl())) {
      // Cancel any in-flight navigation to a different product (e.g. rapid back)
      this.abortController?.abort();
      this.syncPickerDataset(historyUrl);
      return;
    }

    const sectionIds = this.getMainSectionIds();
    if (!sectionIds.length) {
      window.location.assign(historyUrl);
      return;
    }

    this.abortController?.abort();
    const abortController = new AbortController();
    this.abortController = abortController;

    this.isLoading = true;
    document.querySelectorAll('linked-products-picker').forEach((el) => el.classList.add('is-loading'));

    try {
      let sectionsHtml = this.sectionsCache.get(cacheKey);

      if (!sectionsHtml) {
        sectionsHtml = await this.fetchSections(historyUrl, sectionIds, abortController.signal);
        this.sectionsCache.set(cacheKey, sectionsHtml);
      }

      if (abortController.signal.aborted) return;

      const { boostSectionIds } = this.applySections(sectionsHtml);

      // Mark displayed product before history/dataset so popstate can't race wrongly
      this.displayedUrl = cacheKey;

      // Title from the freshly rendered product section
      const heading = document.querySelector('#MainContent h1');
      if (heading?.textContent?.trim()) {
        const shopName = document.documentElement.getAttribute('data-shop-name');
        const titleText = heading.textContent.trim();
        document.title = shopName ? `${titleText} – ${shopName}` : titleText;
      }

      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute('href', this.toProductUrl(historyUrl).href);
      }

      if (updateHistory === 'push') {
        window.history.pushState({ linkedProductSelection: true, url: historyUrl }, '', historyUrl);
      } else if (updateHistory === 'replace') {
        window.history.replaceState({ linkedProductSelection: true, url: historyUrl }, '', historyUrl);
      }

      this.syncPickerDataset(historyUrl);

      // Product DOM + URL are current — remount Boost widgets for this product
      this.reinitializeBoostRecommendations(boostSectionIds, sectionsHtml);

      document.dispatchEvent(
        new CustomEvent('linked-product:loaded', {
          detail: { url: historyUrl, sections: sectionIds },
        })
      );

      window.dispatchEvent(new Event('resize'));

      if (window.Shopify?.PaymentButton?.init) {
        window.Shopify.PaymentButton.init();
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error(error);
      window.location.assign(historyUrl);
    } finally {
      if (this.abortController === abortController) {
        this.isLoading = false;
        this.isHistoryNavigation = false;
        this.abortController = null;
        document.querySelectorAll('linked-products-picker').forEach((el) => el.classList.remove('is-loading'));
      }
    }
  },

  /**
   * Classic JS / module scripts we may re-execute. Never touch JSON / importmap / ld+json.
   * @param {HTMLScriptElement} script
   * @returns {boolean}
   */
  isReinitializableScript(script) {
    const type = (script.getAttribute('type') || 'text/javascript').trim().toLowerCase();

    if (
      type === 'application/json' ||
      type === 'application/ld+json' ||
      type === 'importmap' ||
      type === 'speculationrules' ||
      type === 'text/template' ||
      type === 'text/html'
    ) {
      return false;
    }

    return (
      type === '' ||
      type === 'text/javascript' ||
      type === 'application/javascript' ||
      type === 'text/jscript' ||
      type === 'application/ecmascript' ||
      type === 'text/ecmascript' ||
      type === 'module'
    );
  },

  /**
   * OZtrail-style section reinit: re-execute *inline* scripts by replacing the node
   * (browser runs it). External src scripts are already on the page — skip them.
   * Then bubble shopify:section:load so theme/apps can hydrate.
   * @param {Element} section
   */
  reinitializeSection(section) {
    section.querySelectorAll('script').forEach((oldScript) => {
      if (!(oldScript instanceof HTMLScriptElement)) return;
      if (oldScript.src) return; // OZtrail: only inline scripts
      if (!this.isReinitializableScript(oldScript)) return;

      const newScript = document.createElement('script');

      for (const attr of oldScript.attributes) {
        newScript.setAttribute(attr.name, attr.value);
      }

      let code = oldScript.textContent || '';
      const domReadyMatch = code.match(
        /document\.addEventListener\(\s*['"]DOMContentLoaded['"]\s*,\s*function\s*\(\)\s*\{([\s\S]*)\}\s*\)\s*;?\s*$/
      );
      if (domReadyMatch) {
        code = domReadyMatch[1] || '';
      }
      newScript.textContent = code;

      oldScript.replaceWith(newScript);
    });

    const sectionId = section.id.replace(/^shopify-section-/, '');

    section.dispatchEvent(
      new CustomEvent('shopify:section:load', {
        bubbles: true,
        detail: { sectionId },
      })
    );
  },
};

LinkedProductNavigation.init();

if (!customElements.get('linked-products-picker')) {
  customElements.define(
    'linked-products-picker',
    class LinkedProductsPicker extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', this.#onClick);
        this.addEventListener('pointerenter', this.#onPrefetchIntent, true);
        this.addEventListener('focusin', this.#onPrefetchIntent);

        // Prefer the soft-nav tracker so back/forward never drifts from liquid product.url
        this.dataset.currentProductUrl =
          LinkedProductNavigation.toHistoryUrl(
            LinkedProductNavigation.displayedUrl ||
              this.dataset.currentProductUrl ||
              LinkedProductNavigation.getUrlFromLocation()
          );

        requestAnimationFrame(() => this.#prefetchAllOptions());
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.#onClick);
        this.removeEventListener('pointerenter', this.#onPrefetchIntent, true);
        this.removeEventListener('focusin', this.#onPrefetchIntent);
      }

      #prefetchAllOptions() {
        this.querySelectorAll('a[data-linked-product-url]').forEach((link) => {
          if (!(link instanceof HTMLAnchorElement)) return;
          if (link.classList.contains('is-active')) return;
          const url = link.dataset.linkedProductUrl || link.href;
          if (url) LinkedProductNavigation.prefetch(url);
        });
      }

      /**
       * @param {Event} event
       */
      #onPrefetchIntent = (event) => {
        const link =
          event.target instanceof Element ? event.target.closest('a[data-linked-product-url]') : null;
        if (!link || !(link instanceof HTMLAnchorElement)) return;
        const url = link.dataset.linkedProductUrl || link.href;
        if (url) LinkedProductNavigation.prefetch(url);
      };

      /**
       * @param {MouseEvent} event
       */
      #onClick = (event) => {
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }

        const link = event.target instanceof Element ? event.target.closest('a[data-linked-product-url]') : null;
        if (!link || !(link instanceof HTMLAnchorElement)) return;
        if (link.classList.contains('is-active') || link.getAttribute('aria-current') === 'page') return;

        const url = link.dataset.linkedProductUrl || link.getAttribute('href');
        if (!url) return;

        if (LinkedProductNavigation.isSameProduct(url, this.dataset.currentProductUrl)) {
          return;
        }

        event.preventDefault();
        window.location.assign(url);
      };
    }
  );
}
